import { ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import { cn } from '@/lib/tailwind-utils';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface FilterItem {
  title: string;
  filterComponent: ReactNode;
  isActive?: boolean;
}

interface TableMoreFiltersMenuProps {
  filterItems: FilterItem[];
  disabled?: boolean;
  className?: string;
  activeFiltersCount: number;
  triggerLabel?: string;
  showPlusTrigger?: boolean;
}

export const TableMoreFiltersMenu = ({
  filterItems,
  disabled,
  className,
  activeFiltersCount,
  triggerLabel = 'More filters',
  showPlusTrigger = false,
}: TableMoreFiltersMenuProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeFilterIndex, setActiveFilterIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setActiveFilterIndex(null);
      setHoveredIndex(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button className={cn('h-8 border-dashed', className)} disabled={disabled} variant="outline">
          {showPlusTrigger ? <PlusCircle className="mr-2 h-4 w-4" /> : null}
          {triggerLabel}
          {activeFiltersCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {activeFiltersCount} selected
              </Badge>
            </>
          )}
          {!showPlusTrigger && <ChevronDown className="ml-2 h-4 w-4" />}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        {filterItems.map((item, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center justify-between px-3 py-2 cursor-pointer relative',
              (hoveredIndex === index || activeFilterIndex === index) && 'bg-gray-50',
            )}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() => setActiveFilterIndex(index)}
          >
            <span className="text-sm">{item.title}</span>
            <ChevronRight className="h-4 w-4" />

            {((hoveredIndex === index && activeFilterIndex === null) || activeFilterIndex === index) && (
              <div
                className="absolute left-full top-0 ml-1 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-50"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {item.filterComponent}
              </div>
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};
